/**
 * The transport layer.
 *
 * TWO RULES, both about what a user is allowed to see:
 *
 *   NO RAW ERROR REACHES A COMPONENT. Every failure is mapped to a
 *   `SafeFailureCode` and a sentence written for a founder. A Supabase or
 *   provider message can contain a request URL, and a request URL here carries
 *   the query text.
 *
 *   MOCK MODE MAKES NO CALLS. Not a stubbed call, not a call whose result is
 *   discarded — no `functions.invoke` at all. A demo that quietly spends real
 *   provider budget is a demo that costs money every time somebody opens it.
 */

import { supabase } from '@/lib/supabase/client';
import { reportError } from '@/lib/errors';
import { isMockModeActive } from '@/lib/state/mock-data-provider';
import { KNOWN_FAILURE_CODES, messageFor } from './failure-messages';
import type { MethodologyManifest } from './input-readiness';
import type {
  AnalysisType,
  InputNotReady,
  SafeFailureCode,
  StrategyContextDraft,
} from './types';

/** A failure a component may render. Never an upstream message. */
export class StrategicAnalysisError extends Error {
  readonly code: SafeFailureCode;
  /** Present only for `input_not_ready`, so the UI can open the focused form. */
  readonly readiness?: InputNotReady;

  constructor(code: SafeFailureCode, message: string, readiness?: InputNotReady) {
    super(message);
    this.name = 'StrategicAnalysisError';
    this.code = code;
    this.readiness = readiness;
  }
}

// The copy itself lives in `failure-messages.ts`, with no Supabase import, so a
// component can render a failed run without pulling in the transport.
const KNOWN_CODES = KNOWN_FAILURE_CODES;

export { messageFor };

/**
 * Map any failure onto the safe vocabulary.
 *
 * An unrecognised body becomes `internal_error` rather than being passed
 * through: an error shape nobody designed is exactly the one likely to contain
 * something that should not be displayed.
 */
function toSafeError(payload: unknown, fallback: SafeFailureCode = 'internal_error'): StrategicAnalysisError {
  const body = (payload ?? {}) as Record<string, unknown>;
  const raw = typeof body.error === 'string' ? body.error : typeof body.code === 'string' ? body.code : '';
  const code = (KNOWN_CODES.has(raw) ? raw : fallback) as SafeFailureCode;

  if (code === 'input_not_ready') {
    return new StrategicAnalysisError(code, messageFor(code), {
      code: 'input_not_ready',
      analysis: (body.analysis as AnalysisType | 'all') ?? 'all',
      status: 'missing_required',
      missingRequired: (body.missingRequired as string[]) ?? [],
      unconfirmedRequired: (body.unconfirmedRequired as string[]) ?? [],
      conflicts: (body.conflicts as InputNotReady['conflicts']) ?? [],
      publicationRequired: (body.publicationRequired as string[]) ?? [],
      optionalGaps: [],
      questions: (body.questions as InputNotReady['questions']) ?? [],
    });
  }

  return new StrategicAnalysisError(code, messageFor(code));
}

/**
 * `functions.invoke` returns `data: null` for EVERY non-2xx response —
 * functions-js only decodes the body on the success path, throwing a
 * `FunctionsHttpError` (with the raw, still-unread `Response` on `.context`)
 * the moment `!response.ok`. So the one JSON field a refusal actually carries
 * — `{ error: 'Consent required' }` from `requireConsent()` — is otherwise
 * unreachable here, and every non-2xx collapsed into the same
 * `internal_error`. This reads `.context` directly for exactly the one
 * distinction the screen needs: a 403 consent refusal, so it can open the
 * consent sheet instead of showing a dead-end message.
 */
async function consentRequiredFrom(error: unknown): Promise<boolean> {
  const err = error as { name?: unknown; context?: { status?: unknown; json?: () => Promise<unknown> } };
  if (err?.name !== 'FunctionsHttpError') return false;
  const response = err.context;
  if (!response || response.status !== 403 || typeof response.json !== 'function') return false;
  try {
    const body = await response.json();
    return (body as { error?: unknown } | null)?.error === 'Consent required';
  } catch {
    // A response we could not parse is not a consent refusal we can act on —
    // falls through to the generic internal_error mapping below.
    return false;
  }
}

/** Every call goes through here, so the two rules hold in one place. */
async function invoke<T>(body: Record<string, unknown>, context: string): Promise<T> {
  if (isMockModeActive()) {
    // Not a stub, not a discarded result: demo mode has no network path at all.
    throw new StrategicAnalysisError('feature_disabled', messageFor('feature_disabled'));
  }

  const { data, error } = await supabase.functions.invoke('strategic-analysis', { body });

  if (error) {
    // The transport error is reported for diagnosis and NEVER surfaced.
    reportError(error, { context, silent: true });
    if (await consentRequiredFrom(error)) {
      throw new StrategicAnalysisError('consent_required', messageFor('consent_required'));
    }
    // A non-2xx from the function carries our own safe body; prefer it.
    const safe = toSafeError(data ?? {}, 'internal_error');
    throw safe;
  }

  const payload = (data ?? {}) as Record<string, unknown>;
  if (typeof payload.error === 'string') throw toSafeError(payload);

  return payload as T;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export async function fetchMethodologyManifest() {
  // Typed, because the manifest is what tells the client which inputs live on a
  // BOUND VERSION rather than on the strategy context — and getting that wrong
  // sends a founder to a form that cannot unblock them. `unknown` here meant
  // every caller had to assert its own shape.
  return await invoke<{ manifest: MethodologyManifest }>(
    { action: 'get_methodology_manifest' },
    'strategicAnalysis.fetchMethodologyManifest',
  );
}

/**
 * The draft, the approved version it was assembled against, and THE BOUND
 * VERSION IDS.
 *
 * `boundVersions` is the server's own answer to "which assumption and thesis
 * version is current". Generate sends those exact ids back, so a run binds a
 * version the server just read rather than one the client resolved in a
 * separate query that may have landed a moment later — or, as it did until now,
 * not at all: the screen sent only `strategyContextVersionId`, so Market
 * Validation and Exit Strategy could never bind the version their readiness
 * requires and every request for them was refused.
 */
export interface StrategyContextDraftResponse {
  draft: StrategyContextDraft;
  latestApprovedVersion: { id: string; version: number; sourceFingerprint: string } | null;
  boundVersions: {
    marketAssumptionVersionId: string | null;
    marketAssumptionVersion: number | null;
    exitThesisVersionId: string | null;
    exitThesisVersion: number | null;
  };
}

export async function fetchStrategyContextDraft(startupId: string) {
  return await invoke<StrategyContextDraftResponse>(
    { action: 'get_strategy_context_draft', startupId },
    'strategicAnalysis.fetchStrategyContextDraft',
  );
}

/**
 * What each analysis would fingerprint to from today's approved inputs.
 *
 * Compared against the fingerprint stored on a run to decide staleness. It is a
 * server value on purpose: the hash covers the whole snapshot including the full
 * approved context, and reproducing that canonicalisation here would be a second
 * definition of the same fact, free to drift.
 */
export async function fetchInputFingerprints(startupId: string) {
  return await invoke<{ fingerprints: Record<string, string> }>(
    { action: 'get_input_fingerprints', startupId },
    'strategicAnalysis.fetchInputFingerprints',
  );
}

export async function fetchStrategyContextSource(
  startupId: string,
  strategyContextVersionId: string,
  sourceKey: string,
) {
  return await invoke<{ source: unknown }>(
    { action: 'get_strategy_context_source', startupId, strategyContextVersionId, sourceKey },
    'strategicAnalysis.fetchStrategyContextSource',
  );
}

export async function saveStrategyContext(input: {
  startupId: string;
  draftFingerprint: string;
  selections: { fieldKey: string; candidateId: string; publishToInvestors: boolean }[];
  founderInputs?: { fieldKey: string; value: unknown; sourceUrl?: string }[];
}) {
  return await invoke<{ strategyContextVersionId: string; version: number }>(
    { action: 'save_strategy_context', ...input },
    'strategicAnalysis.saveStrategyContext',
  );
}

export async function saveMarketAssumptions(startupId: string, assumptions: unknown) {
  return await invoke<{ assumptionVersionId: string; version: number }>(
    { action: 'save_market_assumptions', startupId, assumptions },
    'strategicAnalysis.saveMarketAssumptions',
  );
}

export async function saveExitThesis(startupId: string, thesis: unknown) {
  return await invoke<{ exitThesisVersionId: string; version: number }>(
    { action: 'save_exit_thesis', startupId, thesis },
    'strategicAnalysis.saveExitThesis',
  );
}

export async function requestGeneration(input: {
  startupId: string;
  analysisType: AnalysisType;
  strategyContextVersionId: string;
  assumptionVersionId?: string;
  exitThesisVersionId?: string;
}) {
  return await invoke<{ status: 'queued' }>(
    { action: 'generate', ...input },
    'strategicAnalysis.requestGeneration',
  );
}

export { toSafeError as __toSafeErrorForTests };
